import { useMemo } from 'react';
import xss, { IFilterXSSOptions } from 'xss';

// Replaces the old `sanitize-html` integration. xss is pure JS (no postcss,
// no node polyfills) and shaves ~200 kB of parsed bytes off the client
// bundle. The public API surface (SanitizeMode + the four exports) is
// preserved so consumers don't have to change.

export enum SanitizeMode {
  Strict = 'strict',
  TextOnly = 'text',
  Normal = 'normal',
  Partial = 'partial',
  NormalWithImages = 'normalWithImages',
}

// Default tag set used by `Normal` mode. Mirrors sanitize-html's defaults
// (minus a handful of structural tags we never see in CMS content). Keeping
// it explicit means modes that don't override it pick up the same allowlist.
const DEFAULT_TAGS = [
  'a', 'abbr', 'b', 'bdi', 'bdo', 'blockquote', 'br', 'caption', 'cite',
  'code', 'data', 'dd', 'dfn', 'div', 'dl', 'dt', 'em', 'figcaption',
  'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'kbd', 'li',
  'mark', 'ol', 'p', 'pre', 'q', 's', 'samp', 'small', 'span', 'strong',
  'sub', 'sup', 'time', 'u', 'ul', 'var', 'wbr',
] as const;

const IMAGE_AND_DEFAULT_TAGS = [...DEFAULT_TAGS, 'img'];

const buildWhiteList = (tags: ReadonlyArray<string>, attrs: Record<string, string[]> = {}) => {
  const wl: Record<string, string[]> = {};
  tags.forEach((t) => {
    wl[t] = attrs[t] ?? [];
  });
  // Always allow basic anchor attributes alongside whatever else the mode
  // adds (rel is forced via onTagAttr below).
  if (wl.a) wl.a = Array.from(new Set([...wl.a, 'href', 'name', 'target', 'rel']));
  if (wl.img) wl.img = Array.from(new Set([...wl.img, 'src', 'srcset', 'alt', 'title', 'width', 'height', 'loading', 'align']));
  return wl;
};

// Force rel="noopener noreferrer nofollow" on every anchor, replacing
// whatever the source HTML had. Mirrors the simpleTransform behavior in
// the old sanitize-html config.
const forceAnchorRel: IFilterXSSOptions['onTagAttr'] = (tag, name) => {
  if (tag === 'a' && name === 'rel') {
    return 'rel="noopener noreferrer nofollow"';
  }
  return undefined;
};

const finalizeAnchorRel = (html: string): string => (
  // xss's onTagAttr only fires for attributes that ARE present. If the source
  // had no rel attribute at all, we need to inject one. Cheap regex pass.
  html.replace(/<a\b(?![^>]*\brel=)([^>]*)>/gi, '<a$1 rel="noopener noreferrer nofollow">')
);

// Drop the BODY of these tags (not just the tag wrapper) — otherwise xss
// turns `<script>alert(1)</script>` into the literal text `alert(1)`, which
// passes the "no script tag" check but still leaks the malicious payload as
// visible content. Active-content tags get full body removal in every mode.
const STRIP_BODY = ['script', 'style', 'iframe', 'object', 'embed', 'noscript'];

export const options: Record<SanitizeMode, IFilterXSSOptions> = {
  [SanitizeMode.Normal]: {
    whiteList: buildWhiteList(DEFAULT_TAGS),
    stripIgnoreTag: true,
    stripIgnoreTagBody: STRIP_BODY,
    onTagAttr: forceAnchorRel,
  },
  [SanitizeMode.Strict]: {
    whiteList: { a: ['href', 'name', 'target', 'rel'] },
    stripIgnoreTag: true,
    stripIgnoreTagBody: STRIP_BODY,
    onTagAttr: forceAnchorRel,
  },
  [SanitizeMode.TextOnly]: {
    whiteList: {},
    stripIgnoreTag: true,
    stripIgnoreTagBody: STRIP_BODY,
  },
  [SanitizeMode.Partial]: {
    whiteList: buildWhiteList(['b', 'i', 'em', 'strong', 'a', 'h2', 'h3', 'h4', 'h5', 'p', 'span']),
    stripIgnoreTag: true,
    stripIgnoreTagBody: STRIP_BODY,
    onTagAttr: forceAnchorRel,
  },
  [SanitizeMode.NormalWithImages]: {
    whiteList: buildWhiteList(IMAGE_AND_DEFAULT_TAGS),
    stripIgnoreTag: true,
    stripIgnoreTagBody: STRIP_BODY,
    onTagAttr: forceAnchorRel,
  },
};

// CMS-authored long-form content (resort guides, region FAQs, snow
// report comments…) frequently contains empty block-level tags from
// pasted-from-Word or rich-text-editor habits: lone `<p>&nbsp;</p>`
// spacers, `<p></p>` between sections, `<p><br></p>` trailing the body.
// They render as visible blank lines and look like layout bugs. We
// strip them centrally here so every consumer of sanitized() / the
// associated hooks gets the cleanup for free — no per-page fixes.
const EMPTY_INNER = '(?:\\s|&nbsp;|&#160;|\\u00A0|<br\\s*\\/?>)*';
const EMPTY_BLOCK_RE = new RegExp(
  `<(p|div|li|h[1-6])\\b[^>]*>${EMPTY_INNER}<\\/\\1>`,
  'gi',
);

const stripEmptyBlocks = (html: string): string => {
  let prev = html;
  for (let i = 0; i < 5; i += 1) {
    const next = prev.replace(EMPTY_BLOCK_RE, '');
    if (next === prev) return next;
    prev = next;
  }
  return prev;
};

const BREAK_OR_SPACE = '(?:\\s|&nbsp;|&#160;|\\u00A0|<br\\s*\\/?>)';

// Sections frequently arrive with a run of trailing <br> tags after their
// last real content — `…</p><br /><br />` or `…text<br /><br /></p>` — left
// behind by CMS authors pressing Enter a few times. stripEmptyBlocks only
// removes *empty* blocks, so these survive and render as dangling blank
// lines at the end of a section. Strip any run of <br> (plus surrounding
// whitespace / &nbsp;) that sits at the very end of the content, or before
// the run of closing tags that ends it. The lookahead steps over *any*
// closing tag — not just block-level ones — because CMS content pasted from
// Google Sheets wraps every paragraph in `<span data-sheets-root>`, so the
// real-world tail is `…text<br /><br /></span></p>`, not a bare `</p>`.
const TRAILING_BREAKS_RE = new RegExp(
  `${BREAK_OR_SPACE}*<br\\s*\\/?>${BREAK_OR_SPACE}*(?=(?:<\\/[a-zA-Z][a-zA-Z0-9]*>\\s*)*$)`,
  'gi',
);

const stripTrailingBreaks = (html: string): string => {
  let prev = html;
  for (let i = 0; i < 5; i += 1) {
    const next = prev.replace(TRAILING_BREAKS_RE, '');
    if (next === prev) return next;
    prev = next;
  }
  return prev;
};

// The mirror image of the trailing pass: content just as often *starts* with
// a run of <br> tags — `<br /><br /><p>…` before the first block, or
// `<p><span…><br /><br />…` tucked inside its opening wrappers — which render
// as blank lines above the section. Strip a leading <br> run that sits at the
// very start of the content or immediately after the opening wrapper tags. We
// step over any opening tag except the void/break elements (a leading <img> or
// <hr> is real content, so a break after it is a genuine separator, not
// leading whitespace). The captured opening tags (group 1) are preserved, so a
// <br> between two lines of text further in is untouched.
const LEADING_BREAKS_RE = new RegExp(
  `^((?:\\s*<(?!br\\b|hr\\b|img\\b|wbr\\b)[a-zA-Z][a-zA-Z0-9]*\\b[^>]*>)*)${BREAK_OR_SPACE}*<br\\s*\\/?>${BREAK_OR_SPACE}*`,
  'gi',
);

const stripLeadingBreaks = (html: string): string => {
  let prev = html;
  for (let i = 0; i < 5; i += 1) {
    const next = prev.replace(LEADING_BREAKS_RE, '$1');
    if (next === prev) return next;
    prev = next;
  }
  return prev;
};

// Full cleanup pass shared by every sanitize entry point: drop empty
// blocks, then trim <br> runs left at the start and end of a section.
const cleanseHtml = (html: string): string => (
  stripLeadingBreaks(stripTrailingBreaks(stripEmptyBlocks(html)))
);

const runXss = (
  s: string,
  mode: SanitizeMode,
  extendedOptions?: IFilterXSSOptions,
): string => {
  const merged = { ...options[mode], ...extendedOptions };
  const out = xss(s ?? '', merged);
  // Anchors need rel even when source didn't include one. For TextOnly
  // (no tags allowed) the regex pass is a no-op.
  return mode === SanitizeMode.TextOnly ? out : finalizeAnchorRel(out);
};

export const sanitized = (
  s: string,
  mode: SanitizeMode = SanitizeMode.Normal,
  extendedOptions?: IFilterXSSOptions,
) => {
  const clean = cleanseHtml(runXss(s, mode, extendedOptions));
  return { dangerouslySetInnerHTML: { __html: clean } };
};

// CMS copy reaches us HTML-encoded: WordPress' texturizer turns straight
// quotes into curly ones and emits them as entities (`Buyer&#8217;s`), and
// translated locales add accented named entities (`Cha&icirc;ne`). When that
// string is injected as HTML the browser decodes it for free — which is what
// the old markup-based news cards relied on. `textOnly` returns a plain
// string that callers render as a React text child, and React escapes it
// verbatim, so the entity has to be decoded here or it reaches the page as
// literal `&#8217;` text.

// HTML4 names its accented Latin-1 entities as `<base letter><accent>`
// (`eacute`, `uuml`, `ccedil`…), so the whole block is derivable by gluing a
// combining mark onto the base letter and normalizing, instead of spelling
// out sixty near-identical table rows.
const ACCENT_MARKS: Record<string, string> = {
  grave: '\u0300',
  acute: '\u0301',
  circ: '\u0302',
  tilde: '\u0303',
  uml: '\u0308',
  ring: '\u030A',
  cedil: '\u0327',
};

const ACCENT_BASE_LETTERS = 'AaEeIiOoUuYyNnCc';

const buildAccentedEntities = (): Record<string, string> => {
  const out: Record<string, string> = {};
  Object.entries(ACCENT_MARKS).forEach(([accent, mark]) => {
    Array.from(ACCENT_BASE_LETTERS).forEach((letter) => {
      const composed = `${letter}${mark}`.normalize('NFC');
      // Keep only pairs that collapse to a real precomposed character; the
      // rest (`&Cring;`) are not entities and must stay untouched.
      if (composed.length === 1) out[`${letter}${accent}`] = composed;
    });
  });
  return out;
};

// Punctuation and symbols the CMS actually emits by name. Anything outside
// this set is still covered by the numeric branch below, which handles the
// whole Unicode range.
const NAMED_ENTITIES: Record<string, string> = {
  ...buildAccentedEntities(),
  quot: '"',
  apos: "'",
  nbsp: '\u00A0',
  lsquo: '‘',
  rsquo: '’',
  sbquo: '‚',
  ldquo: '“',
  rdquo: '”',
  bdquo: '„',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  bull: '•',
  middot: '·',
  prime: '′',
  Prime: '″',
  laquo: '«',
  raquo: '»',
  lsaquo: '‹',
  rsaquo: '›',
  deg: '°',
  plusmn: '±',
  times: '×',
  divide: '÷',
  frac14: '¼',
  frac12: '½',
  frac34: '¾',
  copy: '©',
  reg: '®',
  trade: '™',
  euro: '€',
  pound: '£',
  yen: '¥',
  cent: '¢',
  sect: '§',
  para: '¶',
  micro: 'µ',
  szlig: 'ß',
  aelig: 'æ',
  AElig: 'Æ',
  oslash: 'ø',
  Oslash: 'Ø',
  aring: 'å',
  Aring: 'Å',
  // `amp` is deliberately absent: it is decoded in its own pass, last, so
  // that `&amp;#8217;` yields the literal text `&#8217;` rather than `’`.
  lt: '<',
  gt: '>',
};

const NUMERIC_ENTITY_RE = /&#(x[0-9a-f]+|[0-9]+);/gi;
const NAMED_ENTITY_RE = /&([a-z][a-z0-9]{1,31});/gi;
const AMP_ENTITY_RE = /&amp;/gi;

const decodeNumericEntity = (match: string, body: string): string => {
  const isHex = body[0] === 'x' || body[0] === 'X';
  const code = isHex ? parseInt(body.slice(1), 16) : parseInt(body, 10);
  // Lone surrogates and out-of-range values make String.fromCodePoint throw,
  // and a NUL would be dropped silently — leave all of them as written.
  if (!Number.isFinite(code) || code <= 0 || code > 0x10FFFF) return match;
  if (code >= 0xD800 && code <= 0xDFFF) return match;
  return String.fromCodePoint(code);
};

// One decode pass, matching what a browser does to a text node exactly once.
// Order matters: numeric first, then named, then `&amp;` — so a
// double-encoded `&amp;lt;` unwinds one level to `&lt;` per pass instead of
// collapsing straight to `<`.
const decodeEntities = (s: string): string => s
  .replace(NUMERIC_ENTITY_RE, decodeNumericEntity)
  .replace(NAMED_ENTITY_RE, (match, name: string) => NAMED_ENTITIES[name] ?? match)
  .replace(AMP_ENTITY_RE, '&');

// Plain text, tags stripped and entities decoded — ready to hand to React as
// a text child or an attribute value (`alt`, `aria-label`, `title`). NOT for
// dangerouslySetInnerHTML: the returned string is decoded, so re-injecting it
// as HTML would undo the escaping. Use `sanitized()` for that.
export const textOnly = (s: string): string => {
  const stripped = decodeEntities(runXss(s, SanitizeMode.TextOnly));
  // Decoding can uncover markup that the source had entity-encoded
  // (`&lt;script&gt;…`), so strip once more when angle brackets appear.
  // Normal copy never takes this branch and is decoded exactly once, which
  // keeps `&amp;#8217;` reading as the literal `&#8217;` a browser would show.
  return stripped.includes('<')
    ? decodeEntities(runXss(stripped, SanitizeMode.TextOnly))
    : stripped;
};

export const useSanitizedText = (
  rawText: string,
  mode: SanitizeMode = SanitizeMode.Normal,
  extendedOptions?: IFilterXSSOptions,
) => (
  useMemo(
    () => cleanseHtml(runXss(rawText, mode, extendedOptions)),
    [rawText, mode],
  )
);

export const useSanitizedHtmlProps = (
  s: string,
  mode: SanitizeMode = SanitizeMode.Normal,
  extendedOptions?: IFilterXSSOptions,
) => (
  useMemo(
    () => sanitized(s, mode, extendedOptions),
    [s, mode, extendedOptions],
  )
);
